// src/pages/Equipment/EquipmentDrawer.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Drawer, Descriptions, Tag, Timeline, Spin,
  Typography, Divider, Space, Button, Empty,
  Modal, Form, Input, Select, Row, Col, message, Dropdown
} from 'antd';
import { HistoryOutlined, InfoCircleOutlined, EditOutlined } from '@ant-design/icons';
import { equipmentAPI, dictAPI, employeesAPI } from '../../api/client';

const { Text } = Typography;

const STATUS_COLORS = {
  'Эксплуатация': 'green',
  'На складе':    'blue',
  'Ремонт':       'orange',
  'Обслуживание': 'purple',
  'Списан':       'red',
};

const ACTION_COLORS = {
  'Выдано сотруднику':        'green',
  'Возврат на склад':         'blue',
  'Передано в ремонт':        'orange',
  'Возврат из ремонта':       'blue',
  'Передано на обслуживание': 'purple',
  'Перемещено':               'cyan',
  'Списано':                  'red',
  'Принято на склад':         'blue',
  'Примечание':               'gray',
};

const ACTION_MENU_ITEMS = [
  { key: 'Выдано сотруднику',        label: 'Выдать сотруднику' },
  { key: 'Возврат на склад',         label: 'Вернуть на склад' },
  { key: 'Передано в ремонт',        label: 'Передать в ремонт' },
  { key: 'Возврат из ремонта',       label: 'Вернуть из ремонта' },
  { key: 'Передано на обслуживание', label: 'На обслуживание' },
  { key: 'Перемещено',               label: 'Переместить' },
  { type: 'divider' },
  { key: 'Примечание',               label: 'Добавить примечание' },
  { key: 'Списано',                  label: 'Списать', danger: true },
];

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function EquipmentDrawer({ equipmentId, onClose, onAction }) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: item, isLoading } = useQuery({
    queryKey: ['equipment-item', equipmentId],
    queryFn:  () => equipmentAPI.getOne(equipmentId).then(r => r.data),
    enabled:  !!equipmentId,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn:  () => dictAPI.getCategories().then(r => r.data),
    enabled:  showEditModal,
  });

  const { data: typesData } = useQuery({
    queryKey: ['types-for-category', editCategoryId],
    queryFn:  () => dictAPI.getTypes(editCategoryId).then(r => r.data),
    enabled:  showEditModal && !!editCategoryId,
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn:  () => dictAPI.getLocations().then(r => r.data),
    enabled:  showEditModal,
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn:  () => dictAPI.getCompanies().then(r => r.data),
    enabled:  showEditModal,
  });

  const { data: employeesData } = useQuery({
    queryKey: ['employees-active'],
    queryFn:  () => employeesAPI.getAll({ is_active: true, limit: 500 }).then(r => r.data),
    enabled:  showEditModal,
  });

  const updateMutation = useMutation({
    mutationFn: (data) => equipmentAPI.update(equipmentId, data),
    onSuccess: () => {
      message.success('Сохранено');
      setShowEditModal(false);
      queryClient.invalidateQueries(['equipment-item', equipmentId]);
      queryClient.invalidateQueries(['equipment']);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const openEdit = () => {
    if (!item) return;
    setEditCategoryId(null);
    form.setFieldsValue({
      inventory_number:  item.inventory_number,
      serial_number:     item.serial_number,
      accounting_number: item.accounting_number,
      name:              item.name,
      cost:              item.cost,
      purchase_date:     item.purchase_date,
      notes:             item.notes,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = () => {
    form.validateFields().then(values => {
      const payload = {};
      if (values.inventory_number  !== item.inventory_number)  payload.inventory_number  = values.inventory_number;
      if (values.serial_number     !== item.serial_number)     payload.serial_number     = values.serial_number     || null;
      if (values.accounting_number !== item.accounting_number) payload.accounting_number = values.accounting_number || null;
      if (values.name              !== item.name)              payload.name              = values.name;
      if (values.cost              !== item.cost)              payload.cost              = values.cost ? parseFloat(values.cost) : null;
      if (values.purchase_date     !== item.purchase_date)     payload.purchase_date     = values.purchase_date     || null;
      if (values.notes             !== item.notes)             payload.notes             = values.notes             || null;
      if (values.location_id)                                  payload.location_id       = values.location_id;
      if (values.company_id)                                   payload.company_id        = values.company_id;
      if (values.responsible_id)                               payload.responsible_id    = values.responsible_id;
      if (values.type_id)                                      payload.type_id           = values.type_id;
      updateMutation.mutate(payload);
    });
  };

  return (
    <>
      <Drawer
        open={!!equipmentId}
        onClose={onClose}
        width={580}
        title={
          item ? (
            <Space>
              <Text style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {item.inventory_number}
              </Text>
              <Tag color={STATUS_COLORS[item.status] || 'default'}>{item.status}</Tag>
            </Space>
          ) : 'Загрузка...'
        }
        extra={
          item && (
            <Space>
              <Button size="small" icon={<EditOutlined />} onClick={openEdit}>
                Редактировать
              </Button>
              <Dropdown
                menu={{
                  items: ACTION_MENU_ITEMS,
                  onClick: ({ key }) => onAction(item, key),
                }}
                trigger={['click']}
              >
                <Button type="primary" size="small">
                  Действие ▾
                </Button>
              </Dropdown>
            </Space>
          )
        }
      >
        {isLoading && <Spin style={{ display: 'block', margin: '40px auto' }} />}

        {item && (
          <>
            <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <InfoCircleOutlined style={{ color: '#1677ff' }} />
              <Text strong>Основная информация</Text>
            </div>

            <Descriptions column={1} size="small" bordered style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Наименование">{item.name}</Descriptions.Item>
              <Descriptions.Item label="Категория">{item.category || '—'}</Descriptions.Item>
              <Descriptions.Item label="Тип">{item.type || '—'}</Descriptions.Item>
              <Descriptions.Item label="Инв. номер">
                <Text code>{item.inventory_number || '—'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Серийный номер">
                <Text code>{item.serial_number || '—'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Бухг. номер">
                <Text code>{item.accounting_number || '—'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Ответственный">
                {item.responsible || <Text type="secondary">Не назначен</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Статус">
                <Tag color={STATUS_COLORS[item.status] || 'default'}>{item.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Место хранения">{item.location || '—'}</Descriptions.Item>
              <Descriptions.Item label="Компания">{item.company || '—'}</Descriptions.Item>
              <Descriptions.Item label="Стоимость">
                {item.cost ? `${Number(item.cost).toLocaleString('ru-RU')} ₽` : '—'}
              </Descriptions.Item>
              {item.purchase_date && (
                <Descriptions.Item label="Дата покупки">
                  {new Date(item.purchase_date).toLocaleDateString('ru-RU')}
                </Descriptions.Item>
              )}
              {item.notes && (
                <Descriptions.Item label="Комментарий">
                  <Text type="secondary">{item.notes}</Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Добавлено">{formatDate(item.created_at)}</Descriptions.Item>
              <Descriptions.Item label="Обновлено">{formatDate(item.updated_at)}</Descriptions.Item>
            </Descriptions>

            <Divider />

            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HistoryOutlined style={{ color: '#1677ff' }} />
              <Text strong>История</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({item.log?.length || 0} событий)
              </Text>
            </div>

            {(!item.log || item.log.length === 0) ? (
              <Empty description="История пуста" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Timeline
                items={item.log.map((entry, i) => ({
                  key:   i,
                  color: ACTION_COLORS[entry.action] || 'gray',
                  children: (
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Space size={4}>
                          <Tag color={ACTION_COLORS[entry.action] || 'default'} style={{ margin: 0 }}>
                            {entry.action}
                          </Tag>
                          {entry.new_status && (
                            <Tag color={STATUS_COLORS[entry.new_status] || 'default'} style={{ margin: 0 }}>
                              → {entry.new_status}
                            </Tag>
                          )}
                        </Space>
                        <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap', marginLeft: 8 }}>
                          {formatDate(entry.created_at)}
                        </Text>
                      </div>
                      {entry.employee && <div style={{ marginTop: 4 }}><Text style={{ fontSize: 13 }}>👤 {entry.employee}</Text></div>}
                      {entry.location  && <div><Text style={{ fontSize: 13 }}>📍 {entry.location}</Text></div>}
                      {entry.notes     && <div><Text type="secondary" style={{ fontSize: 12 }}>{entry.notes}</Text></div>}
                      {entry.created_by && entry.created_by !== 'migration' && (
                        <div><Text type="secondary" style={{ fontSize: 11 }}>✏️ {entry.created_by}</Text></div>
                      )}
                    </div>
                  ),
                }))}
              />
            )}
          </>
        )}
      </Drawer>

      {/* Модалка редактирования */}
      <Modal
        open={showEditModal}
        title={`Редактировать: ${item?.inventory_number} — ${item?.name}`}
        onOk={handleEditSubmit}
        onCancel={() => { setShowEditModal(false); form.resetFields(); }}
        confirmLoading={updateMutation.isPending}
        okText="Сохранить" cancelText="Отмена"
        width={580}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="inventory_number" label="Инвентарный номер">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="serial_number" label="Серийный номер">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="accounting_number" label="Бухгалтерский номер">
                <Input placeholder="Необязательно" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="name" label="Наименование" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="location_id" label="Место хранения">
                <Select showSearch placeholder={item?.location || 'Не изменять'} allowClear
                  options={(locationsData || []).map(l => ({ label: l.name, value: l.id }))}
                  filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="company_id" label="Компания">
                <Select placeholder={item?.company || 'Не изменять'} allowClear
                  options={(companiesData || []).map(c => ({ label: c.name, value: c.id }))} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="responsible_id" label="Ответственный">
            <Select showSearch placeholder={item?.responsible || 'Не изменять'} allowClear
              filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={(employeesData?.items || []).map(e => ({
                label: `${e.full_name}${e.job_title ? ` — ${e.job_title}` : ''}`, value: e.id,
              }))} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="cost" label="Стоимость (₽)">
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="purchase_date" label="Дата покупки">
                <Input placeholder="2024-01-15" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Комментарий">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}